import { SystemRole, hasMinimumRole } from '@peles/shared';

/**
 * Every keyboard shortcut in the app, in one table: the key handler reads it
 * and the "?" help lists it, so a shortcut cannot exist in one and not the
 * other.
 */

/** A page reached with G then a letter. */
export interface GoTarget {
  key: string;
  label: string;
  to: string;
  minRole: SystemRole;
}

export const GO_TARGETS: GoTarget[] = [
  { key: 'd', label: 'Dashboard', to: '/', minRole: SystemRole.PLANNER },
  { key: 'e', label: 'Events', to: '/events', minRole: SystemRole.PLANNER },
  { key: 'c', label: 'Calendar', to: '/calendar', minRole: SystemRole.PLANNER },
  { key: 'v', label: 'Vendors', to: '/vendors', minRole: SystemRole.PLANNER },
  { key: 'l', label: 'Venues (locations)', to: '/venues', minRole: SystemRole.PLANNER },
  { key: 'x', label: 'Expenses', to: '/expenses', minRole: SystemRole.PLANNER },
  { key: 'g', label: 'Guests', to: '/guests', minRole: SystemRole.PLANNER },
  { key: 't', label: 'Tasks', to: '/tasks', minRole: SystemRole.PLANNER },
  { key: 'm', label: 'Communication', to: '/communications', minRole: SystemRole.PLANNER },
  { key: 'f', label: 'Files', to: '/files', minRole: SystemRole.PLANNER },
  { key: 'r', label: 'Reports', to: '/reports', minRole: SystemRole.PLANNER },
  { key: 'i', label: 'Inquiries', to: '/communications/inquiries', minRole: SystemRole.PLANNER },
  { key: 's', label: 'Settings', to: '/settings', minRole: SystemRole.ADMIN },
];

export function goTargetsFor(role: SystemRole | undefined): GoTarget[] {
  return role ? GO_TARGETS.filter((t) => hasMinimumRole(role, t.minRole)) : [];
}

/** How long after G the second letter still counts. */
export const GO_SEQUENCE_MS = 1500;

export const NEW_EVENT_PATH = '/events?new=1';

/** The shortcuts shown in the help, grouped. `keys` are shown as key caps. */
export interface ShortcutHelpRow {
  keys: string[];
  label: string;
}

export function shortcutHelp(
  role: SystemRole | undefined,
  isMac: boolean,
): { title: string; rows: ShortcutHelpRow[] }[] {
  const mod = isMac ? '⌘' : 'Ctrl';
  return [
    {
      title: 'Anywhere',
      rows: [
        { keys: [mod, 'K'], label: 'Search events, vendors and venues' },
        { keys: ['/'], label: 'Search' },
        { keys: ['N'], label: 'New event' },
        { keys: ['?'], label: 'Show these shortcuts' },
      ],
    },
    {
      title: 'Go to',
      rows: goTargetsFor(role).map((t) => ({ keys: ['G', t.key.toUpperCase()], label: t.label })),
    },
    {
      title: 'In search',
      rows: [
        { keys: ['↑', '↓'], label: 'Move through the results' },
        { keys: ['Enter'], label: 'Open' },
        { keys: ['Esc'], label: 'Close' },
      ],
    },
  ];
}

export function isMacPlatform(): boolean {
  return typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform ?? '');
}

/**
 * True while something is open over the page that owns the keyboard: an
 * `EntityModal`, or a menu or popover (`DropdownMenu` renders `role="menu"`,
 * `Popover` renders `role="dialog"`). A shortcut then would act behind it.
 */
export function isOverlayOpen(): boolean {
  return document.querySelector('[role="menu"], [role="dialog"]') !== null;
}

/** True when the key press belongs to a field being typed in. */
export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (tag !== 'INPUT') return false;
  const type = (target as HTMLInputElement).type;
  // Checkboxes, radios and buttons take no text, so shortcuts still work there.
  return !['checkbox', 'radio', 'button', 'submit', 'reset', 'range', 'color'].includes(type);
}
